import { useDispatch } from "react-redux"
import { HandleGalleryFns } from "../../hooks/handleGalleryFns"
import { MyDispatch, MySelector } from "../../redux/store"
import { setCurrentFolder, setCurrentFolderData, setAiSearchActive } from "../../redux/utilSlice"
import { getPhysicalAlbumCount } from "../../utils/getPhysicalAlbumCount"
import { getFilesInAnyAlbum } from "../../utils/getFilesInAnyAlbum"
import { getDesignLabelClass } from "../../gallery-designs/registry"

const GalleryFolderSelectionPhone = ({
  currentFolder,
  setFolderAndReset,
}: {
  currentFolder: string
  setFolderAndReset: (arg: string) => void
}) => {
  const dispatch = useDispatch<MyDispatch>()

  const { setAllPhotos, setAllVideos, setFolderFiles, folders } =
    HandleGalleryFns()

  const { navHeight, aiSearchData } = MySelector((state) => state.util)
  const { data, category, eventId } = MySelector((state) => state.event)
  const { allFiles, albumMemberships } = MySelector((state) => state.gallery)

  const filesInAnyAlbum = getFilesInAnyAlbum(albumMemberships)
  // Folder tab casing follows the active design (e.g. UPPERCASE for editorial).
  const labelClass = getDesignLabelClass(data?.galleryTheme) || "capitalize text-sm"

  const customEventId = "180f7392-4fc1-423c-8f57-fc5913d18049"

  const renderItem = (name: string, id?: string, isLast?: boolean) => {
    // Get physical album count for this folder (host only)
    const physicalCount = category === "host"
      ? getPhysicalAlbumCount(id || null, name, allFiles, filesInAnyAlbum, name === "All Videos")
      : 0

    return (
      <div
        key={id || name}
        className={`flex flex-col gap-2 min-h-10 ${
          isLast ? "py-2 pl-2 pr-4" : "p-2"
        }`}
      >
        <div
          onClick={() => {
            dispatch(
              setCurrentFolder(
                name === "All Photos" || name === "All Videos" || name === "AI Search"
                  ? "common"
                  : "custom"
              )
            )
            setFolderAndReset(name)
            if (name === "AI Search") {
              // AI Search tab selected - will be handled by Gallery component
              dispatch(setCurrentFolderData([]))
              dispatch(setAiSearchActive(true))
            } else if (id) {
              setFolderFiles(id)
            } else if (name === "All Photos") {
              setAllPhotos()
            } else {
              setAllVideos()
            }
          }}
          className={`min-w-max ${labelClass} cursor-pointer ${
            name === currentFolder
              ? "text-newDarkBlack dark:text-golden"
              : "text-newOffBlack dark:text-brightWhite"
          }`}
        >
          <div>{name}</div>
          {category === "host" && physicalCount > 0 && (
            <div className="text-xs opacity-75 lowercase">{physicalCount} selected</div>
          )}
        </div>

        <div
          className={`${
            name === currentFolder
              ? "h-1 bg-newDarkBlack dark:bg-golden rounded-t-md"
              : "h-1 bg-transparent rounded-t-md"
          }`}
        />
      </div>
    )
  }

  return (
    <div
      className="border-b-[0.2px] dark:border-b-[0.5px] z-[505] bg-white/85 dark:bg-black/85 sticky border-b-grey dark:border-b-[#26272c] mb-[15px] w-full overflow-x-scroll overflow-y-hidden px-2 md:hidden"
      style={{
        backdropFilter: "blur(12px)",
        // Overlap the navbar by 1px: navHeight is a rounded offsetHeight while
        // the navbar renders at a fractional height, so a flush top can leave a
        // thin sub-pixel seam. navHeight is a "<n>px" string, so use calc.
        top: navHeight ? `calc(${navHeight} - 1px)` : 0,
      }}
    >
      <div className="flex flex-row gap-2 flex-nowrap whitespace-nowrap items-center">
        {data?.facePrivacy && category === "guest" ? (
          // When facePrivacy is true and user is guest, only show AI Search if enabled, otherwise show nothing
          aiSearchData.aiSearchEnabled ? renderItem("AI Search") : null
        ) : (
          // Show all tabs when facePrivacy is false or user is host
          <>
            {aiSearchData.aiSearchEnabled && renderItem("AI Search")}

            {folders?.map((ef: { id: string; name: string }, index: number) =>
              renderItem(ef?.name, ef?.id, index === folders?.length - 1)
            )}
            {eventId !== customEventId && renderItem("All Photos")}
            {eventId !== customEventId && renderItem("All Videos")}
          </>
        )}
      </div>
    </div>
  )
}

export default GalleryFolderSelectionPhone
